import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { workloadHeat } from "@/lib/heat";
import {
  Loader2, Send, Mail, StickyNote, ClipboardList, ListTodo, CalendarClock, AlertTriangle, IdCard, Check, ChevronDown, ChevronRight, Link2, ExternalLink, Briefcase, Flame, Layers, X, Printer, BellRing,
} from "lucide-react";
import { printPersonCard } from "@/lib/wbsExport";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const FREQ_LABELS = { off: "Off", daily: "Daily", weekly: "Weekly", biweekly: "Every 2 weeks", monthly: "Monthly (1st)" };
const TRACK_COLORS = { on_track: "#10B981", at_risk: "#F59E0B", jeopardy: "#EF4444" };

export default function CardsView({ projectId, scope }) {
  const [cards, setCards] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [phaseFilter, setPhaseFilter] = useState("");

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/cards", { params: projectId ? { project_id: projectId } : scope ? { scope } : {} }),
        projectId ? Promise.resolve({ data: null }) : api.get("/cards/schedule"),
      ]);
      setCards(c.data);
      setSchedule(s.data);
    } catch (e) {
      toast.error("Could not load cards");
      setCards([]);
    }
  };
  useEffect(() => { load(); }, [projectId, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSchedule = async (patch) => {
    const next = { frequency: schedule?.frequency || "off", weekday: schedule?.weekday ?? 0, ...patch };
    try {
      const { data } = await api.put("/cards/schedule", next);
      setSchedule(data);
      toast.success(data.frequency === "off" ? "Card emails turned off" : `Cards will go out ${FREQ_LABELS[data.frequency].toLowerCase()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save schedule");
    }
  };

  const sendAll = async () => {
    setSendingAll(true);
    try {
      const { data } = await api.post("/cards/send", {});
      if (data.errors?.length) toast.error(data.errors[0]);
      else toast.success(`Sent ${data.sent} card(s)${data.skipped ? ` — ${data.skipped} skipped (no email)` : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSendingAll(false);
      load();
    }
  };

  if (!cards) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const sandboxError = (schedule?.last_send_errors || []).some((e) => /verify a domain|testing email|sandbox|example\.com/i.test(e));
  const sentCount = schedule?.last_send_sent ?? schedule?.last_sent_count ?? 0;
  const allPhases = [...new Set(cards.flatMap((c) => (c.projects || []).flatMap((p) => (p.tasks || []).map((t) => t.phase || "No phase"))))];
  const visibleCards = phaseFilter
    ? cards.filter((c) => (c.projects || []).some((p) => (p.tasks || []).some((t) => (t.phase || "No phase") === phaseFilter)))
    : cards;

  return (
    <div className="space-y-6" data-testid="cards-view">
      {!projectId && schedule?.last_send_errors?.length > 0 && (
        <div className={`flex items-start gap-2.5 border rounded-lg px-4 py-3 ${sentCount > 0 ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50"}`} data-testid="cards-send-error-banner">
          <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${sentCount > 0 ? "text-amber-600" : "text-red-600"}`} />
          <div className={`text-sm min-w-0 flex-1 ${sentCount > 0 ? "text-amber-800" : "text-red-800"}`}>
            <p className="font-semibold">{sentCount > 0 ? `Some card emails failed (${sentCount} sent, ${schedule.last_send_errors.length}+ failed).` : "Card emails are failing to send."}</p>
            <p className="text-xs mt-0.5 break-words">{schedule.last_send_errors[0]}{schedule.last_send_errors.length > 1 ? ` (+${schedule.last_send_errors.length - 1} more)` : ""}</p>
            {sandboxError && (
              <p className="text-xs mt-1 font-medium">Your Resend API key is in sandbox mode — it can only email the Resend account owner. Verify your sending domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a> (or switch to a production key) and card emails will flow.</p>
            )}
          </div>
          <Button size="sm" variant="ghost" className={`shrink-0 h-7 gap-1 text-xs font-semibold ${sentCount > 0 ? "text-amber-700 hover:text-amber-900" : "text-red-700 hover:text-red-900"}`}
            onClick={async () => { try { await api.post("/cards/errors/ack"); setSchedule((s) => ({ ...s, last_send_errors: [] })); toast.success("Errors dismissed"); } catch (e) { toast.error("Could not dismiss"); } }}
            data-testid="cards-errors-dismiss">
            <Check className="h-3.5 w-3.5" /> Dismiss
          </Button>
        </div>
      )}
      {!projectId && (
        <div className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg px-4 py-3" data-testid="cards-schedule-bar">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-primary" /> Email cadence</span>
            <Select value={schedule?.frequency || "off"} onValueChange={(v) => saveSchedule({ frequency: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="cards-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FREQ_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {["weekly", "biweekly"].includes(schedule?.frequency) && (
              <Select value={String(schedule?.weekday ?? 0)} onValueChange={(v) => saveSchedule({ weekday: Number(v) })}>
                <SelectTrigger className="h-9 w-36" data-testid="cards-weekday-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {schedule?.last_sent_at && <span className="text-xs text-muted-foreground">Last digest {new Date(schedule.last_sent_at).toLocaleDateString()} · {schedule.last_sent_count} sent</span>}
          </div>
          <Button className="gap-2" onClick={sendAll} disabled={sendingAll || !cards.length} data-testid="cards-send-all">
            {sendingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send all now
          </Button>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="cards-empty">
          <IdCard className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">No open items assigned yet. Cards appear automatically for every person with open tasks or action items.</p>
        </div>
      ) : (
        <>
          {allPhases.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="cards-phase-filter-bar">
              <span className="text-sm font-semibold inline-flex items-center gap-1.5"><Layers className="h-4 w-4 text-primary" /> Phase</span>
              <Select value={phaseFilter || "__all"} onValueChange={(v) => setPhaseFilter(v === "__all" ? "" : v)}>
                <SelectTrigger className="h-9 w-56" data-testid="cards-phase-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All phases</SelectItem>
                  {allPhases.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              {phaseFilter && (
                <span className="text-xs text-muted-foreground" data-testid="cards-phase-filter-count">
                  {visibleCards.length} of {cards.length} resource{cards.length === 1 ? "" : "s"} with tasks in this phase
                </span>
              )}
            </div>
          )}
          {visibleCards.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-8 text-center" data-testid="cards-phase-empty">
              <p className="text-muted-foreground text-sm">No one has open tasks in this phase.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-5 items-start">
              {visibleCards.map((c) => (
                <PersonCard key={c.key} card={c} onChanged={load} projectId={projectId}
                  phaseFilter={phaseFilter || null} onClearPhaseFilter={phaseFilter ? () => setPhaseFilter("") : undefined} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CardActionRow({ a, projectId, nav, onChanged }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (s) => {
    setBusy(true);
    try {
      await api.patch(`/action-items/${a.id}/status`, { status: s });
      toast.success(s === "done" ? "Action item completed" : "Action item updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="group flex items-center gap-2 text-sm rounded px-1 -mx-1 hover:bg-secondary/50 transition-colors" data-testid={`card-action-${a.id}`}>
      <ClipboardList className={`h-3.5 w-3.5 shrink-0 ${a.bucket === "late" ? "text-red-500" : "text-muted-foreground"}`} />
      {a.display_id && <span className="font-mono text-[10px] font-bold text-primary shrink-0">{a.display_id}</span>}
      <span
        role="button" tabIndex={0}
        onClick={() => nav(`/projects/${projectId}?tab=actions`)}
        onKeyDown={(e) => { if (e.key === "Enter") nav(`/projects/${projectId}?tab=actions`); }}
        title="Open the action items register"
        className="truncate flex-1 cursor-pointer group-hover:text-primary group-hover:underline underline-offset-2"
        data-testid={`card-action-open-${a.id}`}
      >{a.title}</span>
      <select
        value={a.status || "open"}
        onChange={(e) => setStatus(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        disabled={busy}
        className="h-6 rounded border border-border bg-background px-1 text-[11px] shrink-0"
        title="Update this action item's status"
        data-testid={`card-action-status-${a.id}`}
      >
        <option value="open">Open</option>
        <option value="in_progress">In progress</option>
        <option value="done">Done</option>
      </select>
      {a.due_date && <span className={`text-xs shrink-0 ${a.bucket === "late" ? "text-red-500 font-bold" : "text-muted-foreground"}`}>{a.due_date}</span>}
    </div>
  );
}

const groupTasksByPhase = (tasks) => {
  const order = [];
  const map = {};
  tasks.forEach((t) => {
    const ph = t.phase || "No phase";
    if (!map[ph]) { map[ph] = []; order.push(ph); }
    map[ph].push(t);
  });
  return order.map((ph) => ({ ph, tasks: map[ph] }));
};

export function PersonCard({ card, onChanged, phaseFilter, onClearPhaseFilter, defaultOpen, projectId }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(!!defaultOpen);
  const [nudging, setNudging] = useState(false);
  const [email, setEmail] = useState(card.email || "");
  const [note, setNote] = useState(card.note || "");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [collapsedPj, setCollapsedPj] = useState({});
  const initials = card.person.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const dirty = email !== (card.email || "") || note !== (card.note || "");
  const allTasks = (card.projects || []).flatMap((p) => p.tasks || []);
  const load = (card.open_tasks || 0) + (card.open_actions || 0);
  const heat = workloadHeat(load);
  const cardStatus = card.overdue > 0 ? "red"
    : allTasks.some((t) => ["at_risk", "jeopardy", "behind"].includes(t.track_status)) ? "yellow" : "green";
  const statusStyle = {
    red: {
      border: "border-red-900/70", solid: "#991B1B", label: "Has overdue tasks",
      header: "linear-gradient(105deg, rgba(255,255,255,0) 38%, rgba(255,255,255,0.20) 46%, rgba(255,255,255,0.05) 54%, rgba(255,255,255,0) 62%), linear-gradient(180deg, #d94f4f 0%, #b91c1c 30%, #8f1414 68%, #6e0f0f 100%)",
    },
    yellow: {
      border: "border-amber-800/70", solid: "#B45309", label: "Running behind (not yet overdue)",
      header: "linear-gradient(105deg, rgba(255,255,255,0) 38%, rgba(255,255,255,0.22) 46%, rgba(255,255,255,0.06) 54%, rgba(255,255,255,0) 62%), linear-gradient(180deg, #f0a83a 0%, #d97706 30%, #a04d06 68%, #7c3a05 100%)",
    },
    green: {
      border: "border-emerald-900/70", solid: "#047857", label: "On track",
      header: "linear-gradient(105deg, rgba(255,255,255,0) 38%, rgba(255,255,255,0.20) 46%, rgba(255,255,255,0.05) 54%, rgba(255,255,255,0) 62%), linear-gradient(180deg, #2fae84 0%, #059669 30%, #036548 68%, #024734 100%)",
    },
  }[cardStatus];

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cards/person", { person: card.person, email, note });
      toast.success(`${card.person}'s card updated`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      if (dirty) await api.put("/cards/person", { person: card.person, email, note });
      const { data } = await api.post("/cards/send", { person: card.person });
      toast.success(data.sent ? `Card emailed to ${card.person}` : "Nothing sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try {
      const { data } = await api.get("/cards/link", { params: { person: card.person } });
      await navigator.clipboard.writeText(data.url);
      toast.success("Update link copied — share it with " + card.person);
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const nudge = async (force) => {
    force = force === true;
    if (!email) { toast.error(`Add an email for ${card.person} first`); return; }
    setNudging(true);
    try {
      if (dirty) await api.put("/cards/person", { person: card.person, email, note });
      const { data } = await api.post("/cards/send", { person: card.person, phase: phaseFilter, project_id: projectId || undefined, force });
      toast.success(data.sent ? `Nudge sent — ${card.person}'s "${phaseFilter}" tasks emailed` : "Nothing sent");
    } catch (e) {
      if (e.response?.status === 409) {
        toast.warning(`${card.person} was already nudged about "${phaseFilter}" this week`, {
          duration: 8000,
          action: { label: "Send anyway", onClick: () => nudge(true) },
        });
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nudge failed");
      }
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className={`border-2 rounded-xl overflow-hidden flex flex-col shadow-[0_6px_18px_rgba(15,23,42,0.25)] ${statusStyle.border}`} data-testid={`person-card-${card.key.replace(/\s/g, "-")}`} data-card-status={cardStatus}>
      <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }} className="card-bevel w-full flex items-center gap-3 px-5 py-4 text-left transition-[filter] cursor-pointer text-white hover:brightness-110" style={{ background: statusStyle.header }} data-testid={`card-toggle-${card.key.replace(/\s/g, "-")}`}>
        {open ? <ChevronDown className="h-4 w-4 text-white/80 shrink-0" /> : <ChevronRight className="h-4 w-4 text-white/80 shrink-0" />}
        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center text-sm font-bold shrink-0 shadow-sm" style={{ color: statusStyle.solid }}>{initials}</div>
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold truncate flex items-center gap-2 text-white">
            <span className="h-2.5 w-2.5 rounded-full shrink-0 bg-white/90" title={statusStyle.label} data-testid={`card-status-dot-${card.key.replace(/\s/g, "-")}`} />
            {card.person}
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-px text-[10px] font-bold text-white shrink-0 ring-2 ring-white/50 shadow-sm"
                  style={{ background: heat.bg }}
                  title={`Workload heat: ${heat.label} — ${load} open item${load === 1 ? "" : "s"} across all projects`}
                  data-testid={`card-load-${card.key.replace(/\s/g, "-")}`}>
              <Flame className="h-3 w-3" /> {heat.label} · {load}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-white/90">
            <span className="inline-flex items-center gap-1"><ListTodo className="h-3 w-3" /> {card.open_tasks} task{card.open_tasks === 1 ? "" : "s"}</span>
            <span className="inline-flex items-center gap-1"><ClipboardList className="h-3 w-3" /> {card.open_actions} action{card.open_actions === 1 ? "" : "s"}</span>
            {card.trueups_due > 0 && <span className="inline-flex items-center gap-1 bg-white/25 rounded-full px-1.5 py-px font-semibold text-white"><CalendarClock className="h-3 w-3" /> {card.trueups_due} true-up{card.trueups_due === 1 ? "" : "s"}</span>}
            {card.overdue > 0 && <span className="inline-flex items-center gap-1 bg-white/25 rounded-full px-1.5 py-px font-bold text-white" data-testid={`card-overdue-${card.key.replace(/\s/g, "-")}`}><AlertTriangle className="h-3 w-3" /> {card.overdue} overdue</span>}
          </div>
        </div>
        <Button size="sm" className="gap-1.5 shrink-0 shadow-sm" onClick={(e) => { e.stopPropagation(); if (!email) { toast.error(`Add an email for ${card.person} first — expand the card to set it`); return; } sendNow(); }} disabled={sending} title={email ? "Email this card now" : "Add an email first"} data-testid={`card-send-${card.key.replace(/\s/g, "-")}`}>
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
        </Button>
      </div>

      {open && (
      <div className="card-expand">
      <div className="min-h-0 overflow-hidden">
      <div className="px-5 py-4 space-y-4 flex-1 border-t border-border">
        {phaseFilter && (
          <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1.5 flex-wrap" data-testid="card-phase-filter-chip">
            <Layers className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="text-xs font-semibold text-primary truncate">Showing only tasks in phase: {phaseFilter}</span>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <button type="button" onClick={nudge} disabled={nudging}
                title={`Email ${card.person} just their "${phaseFilter}" tasks`}
                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold px-2.5 py-1 hover:brightness-110 disabled:opacity-60 transition-[filter]"
                data-testid="card-nudge-btn">
                {nudging ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />} Nudge
              </button>
              {onClearPhaseFilter && (
                <button type="button" onClick={onClearPhaseFilter}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
                  data-testid="card-phase-filter-clear">
                  <X className="h-3 w-3" /> Show all phases
                </button>
              )}
            </div>
          </div>
        )}
        {card.projects.map((pj) => (
          <div key={pj.project_id} data-testid={`card-project-${pj.project_id}`}>
            <button type="button" onClick={() => setCollapsedPj((c) => ({ ...c, [pj.project_id]: !c[pj.project_id] }))}
              className="w-full flex items-center gap-2 rounded-md border-l-4 border-primary bg-primary/5 px-2.5 py-1.5 mb-1.5 hover:bg-primary/10 transition-colors text-left"
              title={collapsedPj[pj.project_id] ? "Expand this project's items" : "Collapse this project's items"}
              data-testid={`card-project-header-${pj.project_id}`}>
              {collapsedPj[pj.project_id] ? <ChevronRight className="h-3 w-3 text-primary shrink-0" /> : <ChevronDown className="h-3 w-3 text-primary shrink-0" />}
              <Briefcase className="h-3 w-3 text-primary shrink-0" />
              <p className="text-[11px] font-bold text-primary truncate uppercase tracking-wide">{pj.project_name}</p>
              <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{pj.tasks.length + pj.actions.length} item{pj.tasks.length + pj.actions.length === 1 ? "" : "s"}</span>
            </button>
            {!collapsedPj[pj.project_id] && (() => {
            const visTasks = phaseFilter ? pj.tasks.filter((t) => (t.phase || "No phase") === phaseFilter) : pj.tasks;
            return (
            <div className="space-y-1">
              {visTasks.length > 0 && (
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1 pt-0.5" data-testid={`card-tasks-header-${pj.project_id}`}>
                  <ListTodo className="h-3 w-3" /> Tasks ({visTasks.length})
                </p>
              )}
              {phaseFilter && visTasks.length === 0 && pj.tasks.length > 0 && (
                <p className="text-xs text-muted-foreground italic" data-testid={`card-no-phase-tasks-${pj.project_id}`}>No tasks in this phase</p>
              )}
              {groupTasksByPhase(visTasks).map((grp) => (
                <div key={grp.ph} data-testid={`card-phase-group-${pj.project_id}-${grp.ph}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-primary/80 flex items-center gap-1 pt-1 pb-0.5">
                    <Layers className="h-3 w-3 shrink-0" /> {grp.ph} <span className="text-muted-foreground normal-case font-normal">({grp.tasks.length})</span>
                  </p>
                  {grp.tasks.map((t) => (
                <div key={t.id} role="button" tabIndex={0}
                     onClick={() => nav(`/projects/${pj.project_id}?tab=plan&task=${t.id}`)}
                     onKeyDown={(e) => { if (e.key === "Enter") nav(`/projects/${pj.project_id}?tab=plan&task=${t.id}`); }}
                     title="Open this task in the plan to verify"
                     className="group flex items-center gap-2 text-sm cursor-pointer rounded px-1 -mx-1 hover:bg-secondary/50 transition-colors"
                     data-testid={`card-task-open-${t.id}`}>
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: t.overdue ? "#EF4444" : TRACK_COLORS[t.track_status] || "#64748b" }} />
                  {t.wbs_id && (
                    <span className="text-[10px] font-mono font-bold text-primary bg-primary/10 rounded px-1 py-px shrink-0" data-testid={`card-task-wbs-${t.id}`}>{t.wbs_id}</span>
                  )}
                  <span className="truncate flex-1 group-hover:text-primary group-hover:underline underline-offset-2">{t.is_milestone ? "◆ " : ""}{t.title}</span>
                  {t.owner_submitted && t.proposed_percent != null && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0"
                          title="They submitted this from their emailed card — confirm it in a True-up"
                          data-testid={`card-proposed-${t.id}`}>
                      submitted {t.proposed_percent}%
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground shrink-0">{t.percent_complete}%</span>
                  {t.end_date && <span className={`text-xs shrink-0 ${t.overdue ? "text-red-500 font-bold" : "text-muted-foreground"}`}>{t.end_date}</span>}
                  <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
                </div>
                  ))}
                </div>
              ))}
              {pj.actions.length > 0 && (
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1 pt-1.5" data-testid={`card-actions-header-${pj.project_id}`}>
                  <ClipboardList className="h-3 w-3" /> Action Items ({pj.actions.length})
                </p>
              )}
              {pj.actions.map((a) => <CardActionRow key={a.id} a={a} projectId={pj.project_id} nav={nav} onChanged={onChanged} />)}
              {pj.trueups.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-600 font-semibold">
                  <CalendarClock className="h-3.5 w-3.5 shrink-0" /> True-up due on {pj.trueups.length} task{pj.trueups.length === 1 ? "" : "s"}
                </div>
              )}
            </div>
            );
            })()}
          </div>
        ))}
      </div>

      <div className="px-5 py-4 border-t border-border space-y-2.5 bg-secondary/20">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com"
            className="h-8 flex-1 min-w-0 rounded border border-border bg-background px-2 text-sm"
            data-testid={`card-email-${card.key.replace(/\s/g, "-")}`}
          />
          {card.email_source === "resource" && email === card.email && <span className="text-[10px] text-muted-foreground shrink-0">from Resources</span>}
        </div>
        <div className="flex items-start gap-2">
          <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-2" />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note for this person (included in the email)…"
            rows={2}
            className="flex-1 min-w-0 rounded border border-border bg-background px-2 py-1.5 text-sm resize-y"
            data-testid={`card-note-${card.key.replace(/\s/g, "-")}`}
          />
        </div>
        {dirty && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={save} disabled={saving} data-testid={`card-save-${card.key.replace(/\s/g, "-")}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
            </Button>
          </div>
        )}
        <button
          type="button"
          onClick={copyLink}
          className="text-xs text-primary font-semibold inline-flex items-center gap-1.5 hover:underline"
          data-testid={`card-copy-link-${card.key.replace(/\s/g, "-")}`}
        >
          <Link2 className="h-3.5 w-3.5" /> Copy update link (no login needed)
        </button>
        <button
          type="button"
          onClick={() => printPersonCard(card, phaseFilter || "")}
          title="Print or save this card as a one-page PDF"
          className="ml-4 text-xs text-primary font-semibold inline-flex items-center gap-1.5 hover:underline"
          data-testid={`card-pdf-${card.key.replace(/\s/g, "-")}`}
        >
          <Printer className="h-3.5 w-3.5" /> Print / PDF
        </button>
      </div>
      </div>
      </div>
      )}
    </div>
  );
}
